import { Typography, AppHeaderBackButton, Button } from '@ace-clublabs/shared-component-library';
import { Header } from './Header';
import { useState } from 'react';
import { useGlobalState } from '../shared/state';
import { useNavigate } from 'react-router-dom';
import { PaymentType } from '@ace-clublabs/payment-types';
import { Pages } from '../shared/pages';
import { ErrorType, ICancelMembershipAutoPayApiRequest, IMembershipProduct, PolicyType } from '../shared/types';
import { PolicyHelper } from '@ace-clublabs/payments-helper';
import { sources } from '../api/sources';
import { UtagHelper, UtagParam } from '../utils/utagHelper';
import DollarIcon from '../assets/dollar-circle.png';
import TowIcon from '../assets/truck-tow.png';
import Spinner from './Spinner';

export const CancelMembership: React.FC = () => {
  return <CancelMembershipView />;
};

export const CancelMembershipView: React.FC = () => {
  const [isLoading, setLoading] = useState(false);
  const [selectedProduct] = useGlobalState('selectedProduct');
  const [clubCode] = useGlobalState('clubCode');
  const [sessionId] = useGlobalState('sessionId');
  const [billPlan] = useGlobalState('billPlan');
  const [brokerRegion] = useGlobalState('brokerRegion');
  const [, setCancelMembershipAutoPayFailed] = useGlobalState('cancelMembershipAutoPayFailed');
  const [, setProductEnrolled] = useState(true);

  const { dueDate, autoPay, policyType } = selectedProduct;
  const navigate = useNavigate();

  const handleCancel = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      const cancelMembershipAutoPayRequest: ICancelMembershipAutoPayApiRequest = {
        productId: selectedProduct.productId.substring(6, 14),
        benefitLevel: (selectedProduct as IMembershipProduct).benefitLevel,
        clubCode: PolicyHelper.getClubCodeFromMemberNumber(selectedProduct.productId),
        brokerRegion,
      };
      await sources.cancelMembershipAutoPay(cancelMembershipAutoPayRequest);
      setProductEnrolled(false);
      setCancelMembershipAutoPayFailed(false);
      UtagHelper.tag({
        ...UtagHelper.CommonTaggingValues,
        clubcode: clubCode,
        sessionid: sessionId,
        page: UtagHelper.Pages.manageAutoPay,
        mem_billplan: billPlan,
        cancelap: 'true',
        flow: 'membership',
      } as UtagParam);
    } catch (e: any) {
      setCancelMembershipAutoPayFailed(true);
    } finally {
      setLoading(false);
      return navigate(Pages.ManageAutoPay);
    }
  };

  const iSTXECMDO = (clubCode === '252' || clubCode === '215') && billPlan.toLowerCase() === 'mp';
  const titleText = iSTXECMDO
    ? 'Auto Pay is required for Monthly Dues Option'
    : 'Are you sure you want to cancel Auto Pay for membership?';
  const buttonText = iSTXECMDO ? 'Cancel Auto Pay and Membership' : 'Cancel Auto Pay';

  const inLockPeriod =
    policyType === PolicyType.Membership &&
    PolicyHelper.getIsMembershipPolicyInLockPeriod(dueDate, autoPay.paymentDetails?.paymentType as PaymentType);

  if (isLoading) {
    return (
      <>
        <Header />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <div className="cancel container">
        <Header />
        <AppHeaderBackButton id="back-button" label="Back" onClick={() => navigate(Pages.ManageAutoPay)} />
        <Typography variant="h5Medium">{titleText}</Typography>
        <div>
          <div className="cancel-items">
            {iSTXECMDO && (
              <Typography variant="b1Regular" id="cancel-text">
                If you cancel Auto Pay, your <strong>membership will be canceled.</strong>
              </Typography>
            )}
          </div>
          <div className="cancel-items">
            <img src={DollarIcon} alt="product-icon"></img>
            <Typography variant="b1Regular">
              With Auto Pay you’ll never miss a payment. Auto Pay ensures your membership stays active.
            </Typography>
          </div>
          <div className="cancel-items">
            <img src={TowIcon} alt="product-icon"></img>
            <Typography variant="b1Regular">
              Maintain uninterrupted access to our legendary 24-hour Roadside Assistance with Auto Pay.
            </Typography>
          </div>
        </div>
        <Button
          variant="contained"
          type="submit"
          fullWidth
          onClick={handleCancel}
          label={buttonText}
          className="cancel-modal-button"
        ></Button>
        {!iSTXECMDO && (
          <Typography variant="b2Regular" color="#707070" className="cancel-items disclaimer">
            If you cancel Auto Pay, you’ll have to resume manual bill payments for your membership.
          </Typography>
        )}
        {inLockPeriod && (
          <Typography variant="b2Regular" color="#707070" className="cancel-items disclaimer">
            Your Auto Pay cancellation will take effect beginning with the next billing cycle. The payment for your
            current bill has already been processed.
          </Typography>
        )}
      </div>
    </>
  );
};
